import { useCartDrawer } from '@iheartjane/cart';
import { useBagPickupMode } from '@iheartjane/cart/utils';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  Accordion,
  Box,
  Drawer,
  Flex,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import type { CartBrandDiscount } from '@iheartjane/types';
import compact from 'lodash-es/compact';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { EMPTY_V3_CART } from '@jane/monolith/components/cart/cartFacade/emptyV3Cart';
import { useCartActions } from '@jane/monolith/components/cart/cartFacade/useCartActions';
import { useCartState } from '@jane/monolith/components/cart/cartFacade/useCartState';
import { CartToppers } from '@jane/monolith/components/cart/cartToppers/cartToppers';
import { CartDrawerAddress } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerAddress/cartDrawerAddress';
import { CartValidationAlert } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerAlerts/cartValidationAlert';
import { CartDrawerDiscountTerms } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerDiscountTerms/cartDrawerDiscountTerms';
import { CartDrawerFooter } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerFooter/cartDrawerFooter';
import { useCartReservationAlert } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerHooks/useCartReservationAlert';
import { useGetCartLineItems } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerHooks/useGetCartLineItems';
import { useReservationAvailability } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerHooks/useReservationAvailability';
import { useValidateCart } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerHooks/useValidateCart';
import { useValidateDeliveryAddress } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerHooks/useValidateDeliveryAddress';
import { CartDrawerItem } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerItems/cartDrawerItem';
import { CartDrawerSpecials } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerSpecials/cartDrawerSpecials';
import { CartDrawerSummary } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerSummary/cartDrawerSummary';
import { CartDrawerToggle } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerToggle/cartDrawerToggle';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import { stopShowBundlePossibilities } from '@jane/monolith/customer/redux/bundlePossibilities';
import { selectCart } from '@jane/monolith/customer/redux/cart';
import { selectCustomer } from '@jane/monolith/customer/redux/customer';
import { selectOperatorEmbedded } from '@jane/monolith/customer/redux/operatorEmbedded';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { useAuth } from '@jane/monolith/hooks/useAuth/useAuth';
import { useGuestCheckoutEnabled } from '@jane/monolith/hooks/useGuestCheckoutEnabled';
import { useSource } from '@jane/monolith/hooks/useSource';
import { getCurrentStoreSpecials } from '@jane/monolith/sources/storeSpecials';
import { JaneGoldCallout } from '@jane/shared-ecomm/components';
import { resolveSelectedMode } from '@jane/shared-ecomm/pages';
import {
  useBrandSpecialsContext,
  useUserPreferences,
} from '@jane/shared-ecomm/providers';
import {
  trackBundlePromptTap,
  trackCheckoutClick,
} from '@jane/shared-ecomm/tracking';
import { getInitialDeliveryAddress } from '@jane/shared-ecomm/util';
import {
  useGetStore,
  useTargetedBrandSpecials,
} from '@jane/shared/data-access';
import { useShouldShowGold } from '@jane/shared/hooks';
// eslint-disable-next-line no-restricted-imports
import type { StoreSpecial } from '@jane/shared/models';
import { useEcommApp } from '@jane/shared/providers';
import { applicableFreeDeliveryConfig } from '@jane/shared/types';
import {
  getBundlesInProgress,
  getEcommPaths,
  monolithPaths as paths,
} from '@jane/shared/util';

import { Divider, FinePrintWrapper } from './cartDrawer.styles';

export const CartDrawer = () => {
  const isMobile = useMobileMediaQuery({});
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useCustomerDispatch();
  const { close: closeCartDrawer } = useCartDrawer();
  const { search } = useLocation();
  const productsListRef = useRef<HTMLUListElement>(null);
  const prevProductCountRef = useRef<number | null>(null);

  const { appMode } = useEcommApp();

  const {
    defaultLogin,
    errors,
    onAppleClick,
    onGoogleClick,
    register,
    resetError,
    resetPassword,
    showAppleLogin,
    showGoogleLogin,
  } = useAuth();

  const authentication = {
    errors,
    login: defaultLogin,
    onAppleClick,
    onGoogleClick,
    register,
    resetError,
    resetPassword,
    showAppleLogin,
    showGoogleLogin,
  };

  // `cart` still comes from the slice; the address has moved to the facade.
  const { cart } = useCustomerSelector(selectCart);
  // The recalculating shimmer follows the facade's narrow in-flight flag; the
  // slice's `isChangingReservationModes` is no longer set once the write flips.
  // The mode comes from there too: `updateReservationMode` writes only the v3
  // cache, so flag-on the slice's copy is never rewritten again.
  const {
    cart: facadeCart,
    deliveryAddress,
    isChangingReservationMode,
  } = useCartState();
  const { removeDeliveryAddress } = useCartActions();
  const { reservation_mode: currentReservationMode } =
    facadeCart ?? EMPTY_V3_CART;
  const bagPickupMode = useBagPickupMode();
  const customer = useCustomerSelector(selectCustomer);
  const operatorEmbedded = useCustomerSelector(selectOperatorEmbedded);

  const {
    brand_discounts,
    brand_discounts_total,
    brand_discounts_service_fee_total,
    brand_discounts_total_with_fee,
    products,
  } = cart;

  const { dismissBanner, dismissedBanners } = useBrandSpecialsContext();
  const qualifiedBanners = brand_discounts.filter(
    (discount) =>
      discount.special_type !== 'product' &&
      !dismissedBanners.includes(discount.brand_special_id)
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const [address, setAddress] = useState(
    getInitialDeliveryAddress(deliveryAddress)
  );
  const { setUserLocation, userPreferences } = useUserPreferences();
  const isGuestCheckoutEnabled = useGuestCheckoutEnabled();
  const {
    isFramelessEmbedMode,
    isBoostForAll,
    isPartnerHosted,
    partnerHostedPath,
  } = useRuntimeConfig();

  const { data: store } = useGetStore(cart.store?.id);
  const storePathProps =
    store && 'id' in store
      ? { id: store?.id, name: store?.name, slug: store?.url_slug }
      : { id: '', name: '', slug: '' };
  const ecommPaths = getEcommPaths(
    {
      appMode,
      isBoostForAll,
      isPartnerHosted,
      operatorId: operatorEmbedded?.operatorId,
      partnerHostedPath,
      storePathProps,
    },
    search
  );

  const hideJanePayBranding = store
    ? Boolean(store?.jane_pay_integration?.white_label) ||
      Boolean(!store?.jane_pay_integration?.enabled)
    : false;

  const authenticated = customer?.authenticated;

  const { data: specialsData } = useSource<{ specials: StoreSpecial[] }>(
    getCurrentStoreSpecials(cart.store?.id)
  );
  const { data } = useTargetedBrandSpecials(
    {
      storeIds: compact([cart.store?.id]),
      appMode,
    },
    { enabled: !!cart.products.length }
  );

  const specials = specialsData ? specialsData.specials : [];

  const bundlesInProgress = getBundlesInProgress({
    brandSpecials: data?.brand_specials || [],
    cart,
  });
  const hasBundlesInProgress =
    bundlesInProgress && bundlesInProgress.length > 0;
  const shouldShowGold =
    useShouldShowGold({ store }) &&
    (!!brand_discounts_total || hasBundlesInProgress);

  const {
    addressErrorMessage,
    deliveryValidationData,
    googleValidatedAddress,
    resetAddressValidation,
  } = useValidateDeliveryAddress({
    store: store!,
    address,
    street2: deliveryAddress?.street2,
  });

  // this is the only way of retrieving customer location data in boost
  // since the marketplace is essentially scoped to a single store and the user is not prompted
  // to share their location when browsing products.
  useEffect(() => {
    if (isFramelessEmbedMode && googleValidatedAddress) {
      // set in local storage
      setUserLocation({
        ...googleValidatedAddress,
        hasResetLocation: true,
      });
    }
  }, [googleValidatedAddress, isFramelessEmbedMode, setUserLocation]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef?.current?.scrollTo(0, 0);
    }
  }, [currentReservationMode]);

  const cartLineItems = useGetCartLineItems();

  const curbsideFlagOn = useFlag(FLAGS.c1CurbsideFulfillment) ?? false;
  const selectedFulfillmentMode = resolveSelectedMode(
    currentReservationMode,
    curbsideFlagOn ? bagPickupMode : undefined
  );
  const isDeliverySelected = selectedFulfillmentMode === 'delivery';

  const effectiveReservationMode = isDeliverySelected
    ? 'delivery'
    : currentReservationMode;

  const {
    canGoToCheckout,
    checkoutButtonLabel,
    curbsideLabel,
    curbsideSchedule,
    deliveryLabel,
    deliverySchedule,
    hasCurbsideSlots,
    hasDeliverySlots,
    hasPickupSlots,
    isAcceptingDeliveryOrders,
    isLoading,
    modesToRender,
    pickupLabel,
    pickupSchedule,
    refetchCurbside,
    refetchDelivery,
    refetchPickup,
    slotOpenModes,
    slotsSettled,
  } = useReservationAvailability({
    address: deliveryAddress,
    reservationMode: selectedFulfillmentMode,
    store: store!,
  });

  const {
    cartReport,
    hasValidCart,
    minimumDeliveryOrderMet,
    minimumPickupOrderMet,
  } = useValidateCart({
    cartLineItems,
    deliveryValidationData,
    reservationMode: effectiveReservationMode,
    store: store!,
  });

  const reservationAlert = useCartReservationAlert({
    curbsideLabel,
    curbsideSchedule,
    deliveryLabel,
    deliverySchedule,
    hasCurbsideSlots,
    hasDeliverySlots,
    hasPickupSlots,
    pickupLabel,
    pickupSchedule,
    reservationMode: selectedFulfillmentMode,
    store: store!,
  });

  // .checkout() is wrapped in an authGate, so the user will be redirected to login if needed.
  const checkoutPath =
    !authenticated && isGuestCheckoutEnabled
      ? paths.guestCheckout()
      : paths.checkout();

  const handleSubmit = async () => {
    dispatch(stopShowBundlePossibilities());

    trackCheckoutClick(effectiveReservationMode);

    if (!isDeliverySelected) {
      removeDeliveryAddress();
    }

    if (canGoToCheckout) {
      closeCartDrawer();
      // The drawer can be opened over checkout itself (e.g. to add a delivery address
      // after switching modes). Re-navigating there would drop the current query
      // params and point the "previous" breadcrumb at checkout itself.
      if (pathname !== checkoutPath) {
        navigate(checkoutPath, {
          state: [{ title: 'previous', url: pathname }],
        });
      }
    }
  };

  const handleSetAddress = (address: string) => {
    setAddress(address);
  };

  const cartSpecials =
    specials.find((special) => special.id === cart.cart_discount_special_id)
      ?.id ?? null;

  const groupSpecial =
    specials.find((special) => special.id === cart.group_special_id)?.id ??
    null;

  const appliedSpecials = compact(
    cart.products
      .map((product) => product.special_id)
      .filter((special, i, arr) => !!special && arr.indexOf(special) === i)
      .concat([cartSpecials, groupSpecial])
  );

  const freeDeliveryMinimum = applicableFreeDeliveryConfig({
    freeDeliveryConfigs: store?.free_delivery_configs,
    discountedSubtotal: cartLineItems?.discountedSubtotal,
  });

  useEffect(() => {
    const prevCount = prevProductCountRef.current;
    const currentCount = products?.length ?? 0;

    // Focus the product list when a product is added or removed while the
    // drawer is already open. Skip the initial render (prevCount is null)
    // so that opening the drawer doesn't scroll past delivery fields.
    if (prevCount !== null && currentCount > 0 && currentCount !== prevCount) {
      productsListRef.current?.focus({ preventScroll: true });
    }

    prevProductCountRef.current = currentCount;
  }, [products?.length]);

  /**
   * Refetch slots when cart drawer opens
   */
  useEffect(() => {
    if (effectiveReservationMode === 'pickup') {
      if (selectedFulfillmentMode === 'curbside') {
        refetchCurbside();
      } else {
        refetchPickup();
      }
    }

    if (isDeliverySelected && address) {
      refetchDelivery();
    }
  }, []);

  return (
    <>
      <Drawer.Header
        divider
        title="Bag"
        subtitle={store?.name ? `${store.name}, ${store.city}` : ''}
      >
        {(!curbsideFlagOn || store) && (
          <CartDrawerToggle
            curbsideLabel={curbsideLabel}
            deliveryAddress={address}
            deliveryLabel={deliveryLabel}
            hasCurbsideSlots={hasCurbsideSlots}
            hasDeliverySlots={hasDeliverySlots}
            hasPickupSlots={hasPickupSlots}
            isAcceptingDeliveryOrders={isAcceptingDeliveryOrders}
            isLoading={isLoading}
            modesToRender={modesToRender}
            pickupLabel={pickupLabel}
            refetchCurbside={refetchCurbside}
            refetchDelivery={refetchDelivery}
            refetchPickup={refetchPickup}
            slotOpenModes={slotOpenModes}
            slotsSettled={slotsSettled}
          />
        )}
      </Drawer.Header>

      <Drawer.Content noPadding ref={contentRef}>
        <Box background="grays-white" height="100%" width="100%">
          <Flex flexDirection="column" height="100%">
            {isDeliverySelected && (
              <CartDrawerAddress
                addressErrorMessage={addressErrorMessage ?? undefined}
                deliveryLabel={deliveryLabel}
                initialDeliveryAddress={address}
                handleSetAddress={handleSetAddress}
                resetAddressValidation={resetAddressValidation}
              />
            )}

            <CartValidationAlert
              brandDiscounts={qualifiedBanners}
              cartReport={cartReport}
              deliveryLabel={deliveryLabel}
              deliveryValidationData={deliveryValidationData}
              minimumDeliveryOrderMet={minimumDeliveryOrderMet}
              minimumPickupOrderMet={minimumPickupOrderMet}
              onDismissBrandDiscountBanner={dismissBanner}
              pickupLabel={pickupLabel}
              reservationAlert={reservationAlert}
              reservationMode={effectiveReservationMode}
              store={store!}
            />

            <Box py={isMobile ? 8 : 24}>
              <ul
                ref={productsListRef}
                aria-label="Products in cart"
                tabIndex={-1}
              >
                {products.map((product) => {
                  const productDetailLink = ecommPaths.menuProduct(product);

                  return (
                    <li key={product?.id + product.price_id}>
                      <CartDrawerItem
                        product={product}
                        productDetailLink={productDetailLink}
                        specials={specials}
                        storeId={cart.store?.id}
                      />
                    </li>
                  );
                })}
              </ul>
            </Box>

            <Accordion>
              {shouldShowGold && (
                <>
                  <Divider />
                  <JaneGoldCallout
                    bundlesInProgress={bundlesInProgress}
                    cartBrandDiscounts={brand_discounts as CartBrandDiscount[]}
                    cartUuid={cart.uuid}
                    discountLabel={brand_discounts_total}
                    feeLabel={brand_discounts_service_fee_total}
                    hideJanePayBranding={hideJanePayBranding}
                    onBrandSpecialClick={(brandSpecial) => {
                      if (brandSpecial.special_type !== 'product') {
                        trackBundlePromptTap({
                          brandSpecial,
                          cityState: `${store?.city}, ${store?.state}`,
                          source: 'bag',
                          storeFulfillmentType:
                            userPreferences?.storeFulfillmentType,
                          storeSearchRadius: userPreferences?.storeSearchRadius,
                          storeType: userPreferences?.storeType,
                          storeId: store?.id,
                          storeName: store?.name,
                          storeCity: store?.city,
                          storeState: store?.state,
                        });
                      }

                      navigate(
                        ecommPaths.menuBrandSpecial({
                          id: brandSpecial.id,
                          title: brandSpecial.title,
                        })
                      );

                      closeCartDrawer();
                    }}
                    storeId={cart.store?.id}
                    totalLabel={brand_discounts_total_with_fee}
                    variant="cart"
                    authentication={authentication}
                  />
                </>
              )}
              <Divider />
              <CartDrawerSpecials specials={specials} />
            </Accordion>

            <CartDrawerSummary
              cartLineItems={cartLineItems}
              isChangingReservationModes={isChangingReservationMode}
            />

            <CartToppers specials={specials} />

            {(appliedSpecials.length > 0 ||
              cartLineItems.discountTotal > 0 ||
              store?.state === 'California') && (
              <FinePrintWrapper flexDirection="column">
                {(appliedSpecials.length > 0 ||
                  cartLineItems.discountTotal > 0) && (
                  <CartDrawerDiscountTerms
                    appliedSpecials={appliedSpecials}
                    freeDeliveryMinimum={
                      isDeliverySelected
                        ? freeDeliveryMinimum?.cart_minimum
                        : undefined
                    }
                    storeId={store?.id}
                    storeName={store?.name}
                    specialLabel={
                      store?.store_compliance_language_settings?.['specials']
                    }
                  />
                )}
              </FinePrintWrapper>
            )}
          </Flex>
        </Box>
      </Drawer.Content>
      <CartDrawerFooter
        checkoutDisabled={!canGoToCheckout || !hasValidCart}
        checkoutButtonLabel={checkoutButtonLabel}
        handleSubmit={handleSubmit}
        total={cartLineItems.total}
        specials={specials}
      />
    </>
  );
};
